import { useState, useCallback, useEffect } from 'react';
import { Button } from '../ui/button';

const WORDS = [
  'confession', 'anonymous', 'midnight', 'forgiveness', 'stranger', 'whisper', 'courage',
  'nostalgia', 'butterfly', 'wanderer', 'silhouette', 'daydream'
];
const ALPHABET = 'abcdefghijklmnopqrstuvwxyz'.split('');
const MAX_MISSES = 6;

export function Hangman({ onScore }) {
  const [word, setWord] = useState('');
  const [guessed, setGuessed] = useState([]);
  const [wins, setWins] = useState(0);
  const [status, setStatus] = useState('playing');

  const newRound = useCallback(() => {
    setWord(WORDS[Math.floor(Math.random() * WORDS.length)]);
    setGuessed([]);
    setStatus('playing');
  }, []);

  useEffect(() => { newRound(); }, [newRound]);

  const misses = guessed.filter((l) => !word.includes(l));
  const solved = word && word.split('').every((l) => guessed.includes(l));

  useEffect(() => {
    if (status !== 'playing' || !word) return;
    if (solved) {
      const next = wins + 1;
      setWins(next);
      setStatus('won');
      onScore(next);
    } else if (misses.length >= MAX_MISSES) {
      setStatus('lost');
      setWins(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guessed, word]);

  const guess = (letter) => {
    if (status !== 'playing' || guessed.includes(letter)) return;
    setGuessed((g) => [...g, letter]);
  };

  return (
    <div className="space-y-4" data-testid="game-hangman">
      <div className="flex items-center justify-between text-sm">
        <span>Win streak: <span className="text-amber-300 tabular-nums" data-testid="hangman-wins">{wins}</span></span>
        <span className="text-muted-foreground">Misses: {misses.length}/{MAX_MISSES}</span>
      </div>

      <p className="text-center text-2xl tracking-[0.35em] py-6 border border-white/10 rounded-lg bg-white/5 break-all" data-testid="hangman-word">
        {word.split('').map((l) => (guessed.includes(l) ? l.toUpperCase() : '_')).join(' ')}
      </p>

      <div className="grid grid-cols-7 sm:grid-cols-9 gap-1.5">
        {ALPHABET.map((letter) => {
          const used = guessed.includes(letter);
          const hit = used && word.includes(letter);
          return (
            <button
              key={letter}
              type="button"
              onClick={() => guess(letter)}
              disabled={used || status !== 'playing'}
              className={`py-2 text-xs uppercase rounded border transition-colors ${
                hit ? 'border-emerald-500/60 bg-emerald-500/20 text-emerald-300'
                  : used ? 'border-rose-500/40 bg-rose-500/10 text-rose-300/60'
                  : 'border-white/10 bg-white/5 hover:border-amber-400/50 hover:bg-amber-400/10'
              }`}
              data-testid={`hangman-key-${letter}`}
            >
              {letter}
            </button>
          );
        })}
      </div>

      {status !== 'playing' && (
        <p className={`text-center text-sm ${status === 'won' ? 'text-emerald-400' : 'text-rose-400'}`} data-testid="hangman-status">
          {status === 'won' ? 'Solved it!' : `Out of tries — the word was "${word}"`}
        </p>
      )}

      <Button variant="outline" className="rounded-full w-full" onClick={newRound} data-testid="hangman-new-word">
        New word
      </Button>
    </div>
  );
}
